/** Display helpers for Payment Terms v2. Money is always EUR (the workpapers are EUR). */

import {toCurrency} from "~/components/currency-component/CurrencyClasses.ts";
import {formatFullCurrency, formatPct} from "~/experiments/pages/payment-terms/paymentTermsUtils.ts";

export const MISSING = '—';

const EUR_SYMBOL = '€';
const EUR_CODE = 'EUR';

/** Chart colours (CSS variables defined in PaymentTermsV2Shared.scss; the fallbacks serve portalled tooltips). */
export const PT_V2_COLORS = {
    atBaseline: 'var(--ptv2-good, rgb(40, 130, 80))',
    belowBaseline: 'var(--ptv2-accent, rgb(45, 111, 245))',
    short: 'var(--ptv2-warn, rgb(196, 98, 16))',
    notSet: 'var(--ptv2-line-2, rgb(218, 222, 230))',
};

/** Colour of a term by net days, matching PtV2TermTag: at/above 60 green, 30+ blue, shorter amber, unknown grey. */
export function termColor(netDays: number | null | undefined): string {
    if (netDays == null) return PT_V2_COLORS.notSet;
    if (netDays >= 60) return PT_V2_COLORS.atBaseline;
    return netDays >= 30 ? PT_V2_COLORS.belowBaseline : PT_V2_COLORS.short;
}

/** Compact money via the shared app formatter (`€ 1.82M`, `€ 866k`). */
export function formatEur(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return MISSING;
    return toCurrency(EUR_SYMBOL, value);
}

/** Whole-euro amount for calculations: `€10,540,558`. */
export function formatEurFull(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return MISSING;
    return formatFullCurrency(value, EUR_CODE);
}

export function formatInt(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return MISSING;
    return Math.round(value).toLocaleString('en-US');
}

/** A 0–1 ratio as a percentage. */
export function formatRatio(value: number | null | undefined, digits = 1): string {
    if (value == null || !Number.isFinite(value)) return MISSING;
    return formatPct(value, digits);
}

/** Prefix approximate counts with a tilde, as the workpapers flag them. */
export function formatApprox(value: number | null | undefined, approximate?: boolean): string {
    const s = formatInt(value);
    return approximate && s !== MISSING ? `~${s}` : s;
}

/**
 * "12 Mar 2026" from an ISO date or datetime.
 * Date-only strings use the same local-calendar parse as production contract explorer.
 * Datetimes keep a real `Date` parse for import timestamps.
 */
export function formatDate(iso: string | null | undefined): string {
    if (!iso) return MISSING;
    const trimmed = iso.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-').map(Number);
        if (!y || !m || !d) return MISSING;
        return new Date(y, m - 1, d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return MISSING;
    return d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
}

/** Net days of a SAP code such as `NT30`; null for a blank code or a non-net code. */
export function termDays(code: string | null | undefined): number | null {
    if (!code) return null;
    const m = /^NT(\d+)$/i.exec(code.trim());
    return m ? Number(m[1]) : null;
}

/** Days the ERP pays early, as a label: `30` or `30–60` for a supplier on two terms. */
export function daysLostLabel(row: {contractDays: number; erpDaysMin: number; erpDaysMax: number}): string {
    const lo = Math.max(0, row.contractDays - row.erpDaysMax);
    const hi = Math.max(0, row.contractDays - row.erpDaysMin);
    return lo === hi ? `${hi}` : `${lo}–${hi}`;
}

/** Days lost used for sorting and calculations: the larger gap. */
export function daysLost(row: {contractDays: number; erpDaysMin: number}): number {
    return Math.max(0, row.contractDays - row.erpDaysMin);
}

/** `Nov 2025` from an ISO date; MISSING when it does not parse. */
export function formatMonth(iso: string | null | undefined): string {
    const m = iso ? /^(\d{4})-(\d{2})/.exec(iso.trim()) : null;
    if (!m) return MISSING;
    return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-GB', {month: 'short', year: 'numeric'});
}

/** `Nov 2025 – Oct 2026`; a single month when both ends fall in it; null without both ends. */
export function periodLabel(start: string | null | undefined, end: string | null | undefined): string | null {
    const s = formatMonth(start);
    const e = formatMonth(end);
    if (s === MISSING || e === MISSING) return null;
    return s === e ? s : `${s} – ${e}`;
}

/** Calendar months covered, counting both ends: Nov 2025 to Oct 2026 is 12. */
export function periodMonths(start: string, end: string): number | null {
    const s = /^(\d{4})-(\d{2})/.exec(start.trim());
    const e = /^(\d{4})-(\d{2})/.exec(end.trim());
    if (!s || !e) return null;
    return (Number(e[1]) - Number(s[1])) * 12 + Number(e[2]) - Number(s[2]) + 1;
}

/** `Last 12 months · Nov 2025 – Oct 2026`, or `7 months · …` for another span; null without a period. */
export function periodCaption(period: {start: string; end: string} | null | undefined): string | null {
    if (!period) return null;
    const label = periodLabel(period.start, period.end);
    if (!label) return null;
    const months = periodMonths(period.start, period.end);
    if (months === 12) return `Last 12 months · ${label}`;
    return months != null && months > 1 ? `${months} months · ${label}` : label;
}
